// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useRef } from 'react';
import { AppIcon } from './AppIcon';
import './DropdownSearchInput.css';

export interface DropdownSearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Compact controlled search input designed to live inside dropdown / filter
 * popovers. Renders an icon-prefixed input with no submit button — debounce
 * is left to the consumer so the component stays reusable.
 *
 * Stops `mousedown` propagation so a popover with an outside-click handler
 * does not close when the user clicks inside the input.
 */
export function DropdownSearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  autoFocus = true,
  ariaLabel,
  className = '',
  'data-testid': dataTestId,
}: DropdownSearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <div
      role="search"
      className={`fin-dropdown-search ${className}`.trim()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span className="fin-dropdown-search__icon" aria-hidden="true">
        <AppIcon name="search" size={16} color="#141414" />
      </span>
      <input
        ref={inputRef}
        type="search"
        className="fin-dropdown-search__input"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        data-testid={dataTestId}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
